import {
    Create,
    TextInput,
    SimpleForm,
    SelectInput,
    required,
    FormDataConsumer,
    ReferenceInput,
    ArrayInput,
    SelectArrayInput,
    useDataProvider,
    AutocompleteInput,
} from 'react-admin';
import { VIDEO_ENCODING, VIDEO_TYPES } from '../../common/constants';
import { InputAdornment } from '@mui/material';
import useAllowedQualities from '../../hooks/useAllowedQualities';
import { useLocation } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { validateNotEmpty } from '../../common/VideoPlayer/util';
import FileUpload from '../../common/inputs/FileUpload';

const validateYouTubeId = (value: any) => {
    const isValid = /^[\w-]{11}$/.test(value);
    return isValid ? undefined : 'Invalid YouTube embed code (expected 11-char video ID)';
};

export const getYouTubeEmbedCode = (data: any): string => {
    if (data.Type !== 'YOUTUBE') return data.Thumbnail || '';
    const url = data.Type === 'YOUTUBE' ? `https://www.youtube.com/embed/${data.rawUrl}` : '';
    const regex =
        /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v=|v\/|.+\?v=)|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(regex);
    const code = match ? match[1] : '';
    return data.Thumbnail || `https://i.ytimg.com/vi/${code}/hq720.jpg`;
};

export const VideoSubjectRender = (choice: any) => `${choice.name}`;

const VideoCreate = () => {
    const Qualities = useAllowedQualities();
    const [searchParams] = useSearchParams();
    const videoSubjectId = searchParams.get('videoSubjectId');
    const dataProvider = useDataProvider();

    const isDisabled = !!videoSubjectId;
    const [categories, setCategories] = useState([]) as any;

    useEffect(() => {
        dataProvider
            .getList('video-subjects', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'name', order: 'ASC' },
                filter: {},
            })
            .then(({ data }) => setCategories(data))
            .catch(error => console.error(error));
    }, [dataProvider]);

    return (
        <Create
            redirect={'/videos'}
            transform={(data: any) => ({
                ...data,
                Thumbnail: getYouTubeEmbedCode(data),
                Status: data.Type === 'YOUTUBE' ? 'PROCESSED' : data.Status,
                ProcessedUrl:
                    data.Type === 'YOUTUBE' ? `https://www.youtube.com/embed/${data.rawUrl}` : '',
                rawUrl:
                    data.Type === 'YOUTUBE'
                        ? `https://www.youtube.com/embed/${data.rawUrl}`
                        : data.rawUrl,
            })}
        >
            <SimpleForm
                defaultValues={{
                    restriction_max_duration_multiple: 10,
                    restriction_max_number_playback: 50,
                    VideoSubjectId: videoSubjectId,
                }}
            >
                <FileUpload type="VIDEO_THUMBNAIL" source="Thumbnail" />
                <TextInput source="Title" fullWidth validate={[required(), validateNotEmpty]} />
                <SelectInput
                    source="Type"
                    onChange={() => {
                        setTimeout(() => {
                            if (document.getElementById('youtube-input')) {
                                document.getElementById('youtube-input')?.focus();
                            }
                        }, 100);
                    }}
                    label={'Type'}
                    fullWidth
                    validate={[required()]}
                    choices={VIDEO_TYPES}
                />

                <FormDataConsumer>
                    {({ formData, ...rest }) =>
                        formData.Type === 'YOUTUBE' ? (
                            <TextInput
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            https://www.youtube.com/embed/
                                        </InputAdornment>
                                    ),
                                }}
                                id={'youtube-input'}
                                label={'Youtube Embed Code'}
                                fullWidth
                                validate={[required(), validateYouTubeId]}
                                source="rawUrl"
                                {...rest}
                            />
                        ) : formData.Type ? (
                            <>
                                <ArrayInput
                                    source="qualities"
                                    validate={[required()]}
                                    defaultValue={['360', '480']}
                                >
                                    <SelectArrayInput
                                        validate={[required()]}
                                        label={'Video Qualities'}
                                        fullWidth
                                        choices={Qualities}
                                    />
                                </ArrayInput>
                                <ArrayInput source="encodings" defaultValue={['HLS']}>
                                    <SelectArrayInput
                                        label={'Video Encodings'}
                                        fullWidth
                                        choices={VIDEO_ENCODING}
                                    />
                                </ArrayInput>

                                <FileUpload
                                    type="video"
                                    meta={{
                                        encodings: formData.encodings,
                                        qualities: formData.qualities,
                                    }}
                                    required={true}
                                    source="rawUrl"
                                    accept="video/*"
                                    multiple={false}
                                />
                            </>
                        ) : (
                            <></>
                        )
                    }
                </FormDataConsumer>

                <AutocompleteInput
                    source="VideoSubjectId"
                    choices={categories}
                    optionText={VideoSubjectRender}
                    fullWidth
                    validate={[required()]}
                    readOnly={isDisabled}
                />
            </SimpleForm>
        </Create>
    );
};

export default VideoCreate;
